/** Rótulos, cores e hooks compartilhados pelas telas de tarefas (lista, quadro, matriz, calendário, painel). */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { Departamento, Membro, MotivoDispensa, MotivoEspera, TarefaDetalhe, TarefaStatus, TipoEntrega } from '@sgc/contracts';
import { api, get, upload } from './api';
import { useToast } from '../ui/toast';

export const ROTULO_STATUS: Record<TarefaStatus, string> = {
  PENDENTE: 'Pendente', EM_ANDAMENTO: 'Em andamento', AGUARDANDO_TERCEIRO: 'Aguardando', EM_REVISAO: 'Em revisão', CONCLUIDA: 'Concluída', DISPENSADA: 'Dispensada', CANCELADA: 'Cancelada',
};
export type CorPill = 'verde' | 'ambar' | 'verm' | 'azul' | 'acento' | undefined;
export const COR_STATUS: Record<TarefaStatus, CorPill> = {
  PENDENTE: undefined, EM_ANDAMENTO: 'azul', AGUARDANDO_TERCEIRO: 'ambar', EM_REVISAO: 'acento', CONCLUIDA: 'verde', DISPENSADA: undefined, CANCELADA: 'verm',
};
/** Verbo do botão que leva a tarefa para cada status. */
export const ACAO_STATUS: Record<TarefaStatus, string> = {
  PENDENTE: 'Voltar a pendente', EM_ANDAMENTO: 'Iniciar', AGUARDANDO_TERCEIRO: 'Aguardar terceiro', EM_REVISAO: 'Enviar p/ revisão', CONCLUIDA: 'Concluir', DISPENSADA: 'Dispensar', CANCELADA: 'Cancelar',
};
export const ROTULO_ESPERA: Record<MotivoEspera, string> = { CLIENTE: 'Cliente', DOCUMENTO: 'Documento', GOVERNO: 'Órgão / sistema do governo', INTERNO: 'Interno (outro setor)' };
export const ROTULO_DISPENSA: Record<MotivoDispensa, string> = {
  SEM_MOVIMENTO: 'Sem movimento no período', NAO_APLICAVEL: 'Não se aplica a este cliente', ENTREGUE_PELO_CLIENTE: 'Entregue pelo próprio cliente',
  ENTREGA_CENTRALIZADA: 'Entrega centralizada (matriz / outro escritório)', FORA_DE_ESCOPO: 'Fora do escopo do contrato', OUTRO: 'Outro (exige supervisor)',
};
export const ROTULO_EVENTO: Record<string, string> = {
  CRIADA: 'criada', STATUS: 'status', ATRIBUIDA: 'responsável', PRAZO_RECALCULADO: 'prazo recalculado', PRAZO_DIVERGENTE: 'prazo divergente',
  VENCIMENTO_MANUAL: 'vencimento manual', COMENTARIO: 'comentário', ENTREGA: 'entrega', CANCELADA: 'cancelada', REABERTA: 'reaberta',
};
export const STATUS_ABERTOS_UI: readonly TarefaStatus[] = ['PENDENTE', 'EM_ANDAMENTO', 'AGUARDANDO_TERCEIRO', 'EM_REVISAO'];

export function useMembros() {
  return useQuery({ queryKey: ['membros'], queryFn: () => get<Membro[]>('/v1/membros'), staleTime: 60_000 });
}
export function useDepartamentos() {
  return useQuery({ queryKey: ['departamentos'], queryFn: () => get<Departamento[]>('/v1/departamentos'), staleTime: 60_000 });
}
export const opcoesMembros = (ms: Membro[] | undefined) => (ms ?? []).filter((m) => m.ativo).map((m) => [m.id, m.apelido ? `${m.apelido} · ${m.nome}` : m.nome] as const);

/** Depois de qualquer ação numa tarefa, todas as visões precisam recarregar. */
export function useInvalidarTarefas() {
  const qc = useQueryClient();
  return useCallback((id?: string) => {
    for (const k of ['tarefas', 'matriz', 'painel', 'calendario']) void qc.invalidateQueries({ queryKey: [k] });
    if (id) void qc.invalidateQueries({ queryKey: ['tarefa', id] });
  }, [qc]);
}

export function fmtDataHora(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

export const ROTULO_TIPO_ENTREGA: Record<TipoEntrega, string> = { DECLARACAO: 'Declaração (protocolo)', GUIA: 'Guia de pagamento', DOCUMENTO: 'Documento entregue' };

export function fmtMinutos(min: number): string {
  const h = Math.floor(min / 60); const m = min % 60;
  return h ? `${h}h${m ? String(m).padStart(2, '0') : ''}` : `${m} min`;
}
export function fmtBytes(n: number): string {
  return n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`;
}
export const fmtMoeda = (v: number | null | undefined) => (v == null ? '' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));

/**
 * Ação sobre o detalhe: toda rota do detalhe devolve a TarefaDetalhe nova —
 * gravamos direto no cache e invalidamos as listas.
 */
export function useAcaoDetalhe(id: string) {
  const qc = useQueryClient();
  const toast = useToast();
  const invalidar = useInvalidarTarefas();
  return useMutation({
    mutationFn: ({ metodo, rota, body, arquivo }: { metodo?: 'POST' | 'PUT' | 'PATCH' | 'DELETE'; rota: string; body?: unknown; arquivo?: File }) =>
      arquivo ? upload<TarefaDetalhe>(`/v1/tarefas/${id}${rota}`, arquivo) : api<TarefaDetalhe>(`/v1/tarefas/${id}${rota}`, { method: metodo ?? 'POST', body }),
    onSuccess: (t) => { qc.setQueryData(['tarefa', id], t); invalidar(); },
    onError: (e) => toast.erro(e),
  });
}

/** Prazo interno vencido com o vencimento real ainda por vir: atenção, não atraso. */
export function alertaInterno(t: { internoVencido?: boolean; diasInterno?: number }): string | null {
  if (!t.internoVencido) return null;
  const n = -(t.diasInterno ?? -1);
  return `prazo interno venceu há ${n} dia${n === 1 ? '' : 's'}`;
}

/** Texto curto de prazo: "3 dias de atraso" / "hoje" / "em 2 dias". */
export function textoPrazo(dias: number, aberta: boolean): { texto: string; cor: CorPill } | null {
  if (!aberta) return null;
  if (dias < 0) return { texto: `${-dias} dia${dias === -1 ? '' : 's'} de atraso`, cor: 'verm' };
  if (dias === 0) return { texto: 'vence hoje', cor: 'ambar' };
  if (dias <= 3) return { texto: `em ${dias} dia${dias === 1 ? '' : 's'}`, cor: 'ambar' };
  return null;
}
